import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { defer, take, tap } from 'rxjs';
import { StatusBarState } from '../core/components/status-bar/status-bar.state';
import { DuplicatiServer, NotificationDto } from '../core/openapi';
import { ServerStateService } from '../core/services/server-state.service';
import { ServerStatusWebSocketService } from '../core/services/server-status-websocket.service';
import { SysinfoState } from '../core/states/sysinfo.state';

@Injectable({
  providedIn: 'root',
})
export class NotificationsState {
  #dupServer = inject(DuplicatiServer);
  #statusBarState = inject(StatusBarState);
  #serverState = inject(ServerStateService);
  #sysinfo = inject(SysinfoState);
  #wsService = inject(ServerStatusWebSocketService);

  serverState = this.#statusBarState.serverState;

  serverStateEffect = effect(() => {
    const serverState = this.#statusBarState.serverState();

    if (serverState?.LastNotificationUpdateID) {
      this.updateLastNotificationEventId(serverState?.LastNotificationUpdateID);
    }
  });

  #lastNotificationEventId = -1;
  #notificationStream = signal<NotificationDto[]>([]);

  notifications = this.#notificationStream.asReadonly();
  pendingRefresh = false;

  constructor() {
    this.#wsService.subscribe('notifications');
  }

  init() {
    if (this.notifications().length === 0) {
      this.getNotifications();
    }
  }

  notificationsEffect = effect(() => {
    const notifications = this.#wsService.notificationState();
    if (!notifications) return;

    this.#notificationStream.set(notifications);
  });

  pendingRefreshEffect = effect(() => {
    const isSet = this.#serverState.isConnectionMethodSet();
    if (!isSet) return;

    if (this.pendingRefresh) {
      this.pendingRefresh = false;
      this.getNotifications();
    }
  });

  #usingWebsocket = computed(() => {
    const isUsingWs = this.#serverState.getConnectionMethod() === 'websocket';
    const hasWsRemote = this.#sysinfo.hasWsRemoteControl();
    return isUsingWs && hasWsRemote;
  });

  updateLastNotificationEventId(eventId: number) {
    const currentEventId = this.#lastNotificationEventId;

    if (currentEventId === eventId) return;

    this.#lastNotificationEventId = eventId;
    this.getNotifications();
  }

  getNotifications() {
    if (this.#usingWebsocket()) return;
    if (!this.#serverState.isConnectionMethodSet()) {
      this.pendingRefresh = true;
      return;
    }

    defer(() => this.#dupServer.getApiV1Notifications())
      .pipe(
        take(1),
        tap((notifications) => this.#notificationStream.set(notifications))
      )
      .subscribe();
  }

  getNofication(notificationId: number) {
    defer(() =>
      this.#dupServer.getApiV1NotificationById({
        path: {
          id: notificationId,
        },
      })
    )
      .pipe(take(1))
      .subscribe();
  }

  deleteNotification(notificationIndex: number) {
    const notifications = this.#notificationStream();
    const notification = notifications[notificationIndex];

    this.#notificationStream.set(notifications.filter((_, index) => index !== notificationIndex));

    defer(() =>
      this.#dupServer.deleteApiV1NotificationById({
        path: {
          id: notification.ID!,
        },
      })
    )
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.#notificationStream.set(notifications);
        },
      });
  }

  deleteAllNotifications() {
    const notifications = this.#notificationStream();

    this.#notificationStream.set([]);

    // One request for all notifications, as one request per notification floods the browser (duplicati#4719)
    defer(() => this.#dupServer.deleteApiV1Notifications())
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.#notificationStream.set(notifications);
        },
      });
  }
}
